/*
  <collapsible-tabs> — GB. Adds exactly ONE behaviour on top of <multicolumn-tabs>:
  when "Load all" is on, clicking the already-active tab deactivates it and puts
  the section back into its load state — every panel visible, no tab active.

  Deliberately a WRAPPER, not a fork. <multicolumn-tabs> keeps doing all the work
  (click, roving tabindex, arrows, Home/End, panel hiding) and this element only
  listens. That is why assets/multicolumn-tabs.js, which is shared with the
  Multicolumn and Featured Ingredients sections, needed no edit at all.

  Ordering is the whole trick. multicolumn-tabs binds its click handler on each
  TAB element, so:
    - a CAPTURE listener here runs BEFORE it (root -> target) and can record
      whether that tab was already selected;
    - a BUBBLE listener here runs AFTER it (target -> root) and can undo the
      selection it just made.
  No event is cancelled, so multicolumn-tabs is never interfered with.

  Only rendered when Enable tabs AND Load all are both on, so it costs nothing
  anywhere else. Deferred, self-registering, no dependencies.
*/
if (!customElements.get('collapsible-tabs')) {
  customElements.define(
    'collapsible-tabs',
    class CollapsibleTabs extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
        if (!this.tabs.length || !this.panels.length) return;

        this.wasActive = false;
        this.openFirst = this.hasAttribute('data-open-first');

        // Capture: runs before multicolumn-tabs selects the tab.
        this.addEventListener(
          'click',
          (event) => {
            const tab = event.target.closest('[role="tab"]');
            this.wasActive = !!tab && tab.getAttribute('aria-selected') === 'true';
          },
          true
        );

        // Bubble: runs after it. If the tab was already active, this was a
        // second click on the same tab, so undo the selection.
        this.addEventListener('click', (event) => {
          const tab = event.target.closest('[role="tab"]');
          if (!tab) return;

          if (this.wasActive) {
            this.wasActive = false;
            this.showAll(tab);
            return;
          }

          // A panel was just shown. Mirror what the server does on load, so the
          // first row of the tab you land on is the one that is open. Without
          // this the panel opens fully collapsed, because only the FIRST panel
          // is server-rendered open (see cc_allow_open in the section).
          this.openFirstRow(this.panels[this.tabs.indexOf(tab)]);
        });

        // Arrow / Home / End move the selection through multicolumn-tabs'
        // activate() without ever firing a click, so the handler above never
        // sees them. Same bubble trick: this runs after it has moved the
        // selection, so reading aria-selected gives the NEW tab.
        this.addEventListener('keydown', (event) => {
          const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
          if (!keys.includes(event.key)) return;
          const index = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
          if (index > -1) this.openFirstRow(this.panels[index]);
        });
      }

      /*
        Open the first row of `panel` and close the first row of every other
        panel, so exactly one is open at a time. Only touches each panel's FIRST
        <details>; rows the visitor opened further down are left alone.
      */
      openFirstRow(panel) {
        if (!this.openFirst || !panel) return;
        this.panels.forEach((p) => {
          const first = p.querySelector('details');
          if (first) first.open = p === panel;
        });
      }

      /*
        Restore the exact state the section first rendered in: every panel
        visible, no tab selected, no active pill.

        focused keeps roving tabindex correct. A tablist must have exactly one
        tab with tabindex 0, and focus is sitting on the tab that was just
        clicked, so that is the one that keeps it. Falls back to the first tab.
      */
      showAll(focused) {
        this.panels.forEach((panel) => panel.removeAttribute('hidden'));
        this.tabs.forEach((tab, index) => {
          tab.setAttribute('aria-selected', 'false');
          tab.classList.remove('is-active');
          const keep = focused ? tab === focused : index === 0;
          tab.setAttribute('tabindex', keep ? '0' : '-1');
        });
        // Back to the load state means the open row goes back to the load state
        // too: the first row of the first panel, and only that one.
        this.openFirstRow(this.panels[0]);
      }
    }
  );
}
